/// <amd-module name="Core/Medius.Core.Web/Scripts/components/sendEmailDialog/sendEmailDialogForm"/>
import * as React from "react";
import { Button, ButtonGroup, Checkbox, DialogHeaderRenderProps, DialogPart, FlexContent, FlexLayout, FlexSpacer, IconCommunicationRequiredRegular, IconStatusErrorRegular, ResponsiveGridLayout, TextArea, TextField, Text } from "@medius/ui-controls";
import { translate, getLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { getRegex } from "Core/Medius.Core.Web/Scripts/Medius/core/validation/email";
import { SendEmailDialogFormFields } from "./types";
import { MultiSelect, MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";

export interface SendEmailDialogFormProps {
    addressBook: string[];
    recipients: string[];
    ccs: string[];
    sendMeACopy: boolean;
    subject: string;
    body: string;
    headerProps: DialogHeaderRenderProps;
    errorMessage?: string;
    onSendAction: (fields: SendEmailDialogFormFields) => void;
    onRequestClose: () => void;
}

export const SendEmailDialogForm = (props: SendEmailDialogFormProps) => {
    const [recipients, setRecipients] = React.useState<string[]>(props.recipients);
    const [ccs, setCcs] = React.useState<string[]>(props.ccs);
    const [sendMeACopy, setSendMeACopy] = React.useState<boolean>(props.sendMeACopy);
    const [subject, setSubject] = React.useState<string>(props.subject);
    const [body, setBody] = React.useState<string>(props.body);

    const validateEmail = (values: string[]): boolean | string => {
        const emailRegex = getRegex();
        return values.every(value => emailRegex.test(value));
    };

    const onRecipientsChange = (event: MultiSelectChangeEvent) => {
        setRecipients([...event.value]);
    };
    
    const onCcsChange = (event: MultiSelectChangeEvent) => {
        setCcs([...event.value]);
    };

    const isNullOrEmpty = (array: string[]) => {
        return !array || array.length === 0;
    };

    const renderErrorItem = (text: string) => {
        return (
            <FlexLayout
                itemColumnSpacing="vsmall"
                wrapItems={false}
                verticalAlignment="top"
            >
                <IconStatusErrorRegular color="error" />
                <Text
                    variant="small"
                    feedbackType="error"
                >
                    {text}
                </Text>
            </FlexLayout>
        );
    };
        
    return (
        <>
            <DialogPart.Title {...props.headerProps} />
            <DialogPart.Content width={"large"}>
                <ResponsiveGridLayout 
                    maxColumns={1}
                    columnWidth={7}
                    itemRowSpacing="medium"
                >
                    <fieldset>
                        <FlexLayout spacerMode="on" spacerSize="small" width="fill">
                            <FlexContent>
                                <fieldset>
                                    <div>{translate("#Core/emailTo")}: {isNullOrEmpty(recipients) && <IconCommunicationRequiredRegular size="vsmall" color="error" aria-hidden={true} />}</div>
                                    <MultiSelect
                                        data={props.addressBook}
                                        onChange={onRecipientsChange}
                                        value={recipients}
                                        allowCustom={true}
                                    />
                                    {isNullOrEmpty(recipients) && renderErrorItem(getLabelTranslation("#Core/formFieldRequiredErrorMessage", "Recipients field"))}
                                    {!validateEmail(recipients) && renderErrorItem(translate("#Core/defaultValidation_EMAILATTRIBUTE"))}
                                </fieldset>
                            </FlexContent>
                        </FlexLayout>
                        <FlexLayout spacerMode="on" spacerSize="small" width="fill">
                            <FlexContent>
                                <fieldset>
                                    <div>{translate("#Core/emailCc")}:</div>
                                    <MultiSelect
                                        data={props.addressBook}
                                        onChange={onCcsChange}
                                        value={ccs}
                                        allowCustom={true}
                                        />
                                    {!validateEmail(ccs) && renderErrorItem(translate("#Core/defaultValidation_EMAILATTRIBUTE"))}
                                </fieldset>
                            </FlexContent>
                        </FlexLayout>
                        {props.errorMessage && renderErrorItem(props.errorMessage)}
                    </fieldset>
                    <FlexLayout spacerMode="on" spacerSize="small" width="content">
                        <Checkbox
                            label={translate("#Core/emailSendMeCopy")}
                            onChange={(e) => setSendMeACopy(e.target.value)}
                            value={sendMeACopy}
                            data-testid="send-email-copy-send-me-copy"
                        />
                    </FlexLayout>
                    <FlexLayout spacerMode="on" spacerSize="small" width="fill">
                        <FlexContent>
                            <TextField
                                isRequired
                                fieldLabel={translate("#Core/emailMessageSubject")}
                                errorMessages={!subject ? getLabelTranslation("#Core/formFieldRequiredErrorMessage", "Subject field") : []}
                                inputProps={{
                                    onChange:(e) => setSubject(e.target.value),
                                    value: subject,
                                    height: "medium",
                                }}
                                data-testid="send-email-dialog-subject"
                            />
                        </FlexContent>
                    </FlexLayout>
                    <FlexLayout spacerMode="on" spacerSize="small" width="fill">
                        <div style={{width: "100%", height: "280px"}}>
                            <TextArea
                                width="fill"
                                height="fill"
                                onChange={(e) => setBody(e.target.value)}
                                value={body}
                                data-testid="send-email-dialog-body"
                            />
                        </div>
                    </FlexLayout>
                </ResponsiveGridLayout>
            </DialogPart.Content>
            <DialogPart.Footer>
                <FlexLayout>
                    <FlexSpacer />
                    <ButtonGroup>
                        <Button
                            data-testid="send-email-cancel-button"
                            label={translate("#Core/cancel")}
                            variant="secondary"
                            onClick={props.onRequestClose}
                        />
                        <Button
                            data-testid="send-email-send-button"
                            label={translate("#Core/sendEmail")}
                            onClick={() => props.onSendAction({
                                recipients,
                                ccs,
                                sendMeACopy,
                                subject,
                                body,
                            })}
                            disabled={isNullOrEmpty(recipients) || !validateEmail(recipients) || !validateEmail(ccs) || !subject || !body}
                        />
                    </ButtonGroup>
                </FlexLayout>
            </DialogPart.Footer>
        </>
    );
};